import { writable, derived } from 'svelte/store';

export const mapMultiSelectMode = writable(false);

/**
 * Set once a request has been answered with 401, which means the session has expired. The
 * app watches this to show the login form right away, instead of leaving an interface up
 * where every action fails.
 */
export const sessionExpired = writable(false);

/**
 * Whether a dialog is open on the map. On a narrow screen the sidebar steps aside while one
 * is, since there isn't room for both, and comes back when it closes. Kept here rather than
 * in the map so that the shell can lay itself out without reaching into it.
 */
export const mapPopupOpen = writable(false);

/**
 * Whether a new playground is being put in place. It is positioned by dragging a marker
 * around, and its dialog only opens once the marker is tapped, so this covers the stretch
 * before there is any dialog to go by. The sidebar steps aside for it the same way, since
 * deciding where a playground goes is the part that needs to see the map.
 */
export const mapPlacingPlayground = writable(false);

/**
 * Whether something is being done on the map: a dialog is open on it, a new playground is
 * being put in place, or playgrounds are being picked out to merge. On a narrow screen the
 * sidebar has the screen to itself until one of these starts, hands it to the map for as
 * long as it lasts, and takes it back when it is over.
 *
 * The three of them are gathered here so that the shell and the map read the same answer,
 * and so that whatever starts one of them, a tap in the inbox or a button in the toolbar,
 * doesn't have to know about the layout.
 */
export const mapTaskInProgress = derived(
    [mapPopupOpen, mapPlacingPlayground, mapMultiSelectMode],
    ([$popupOpen, $placing, $selecting]) => $popupOpen || $placing || $selecting
);

/**
 * Whether the map has been asked for from the toolbar with nothing in particular to do on
 * it. The tasks above bring it forward on their own and put it away when they are done,
 * which leaves no way to simply look at the map, so this is that way. It stays set until it
 * is turned off again: coming out of a dialog opened while looking around lands back on the
 * map rather than back in the inbox.
 */
export const mapRequested = writable(false);

/**
 * Whether the map is on show, for either reason.
 */
export const mapInUse = derived(
    [mapTaskInProgress, mapRequested],
    ([$taskInProgress, $requested]) => $taskInProgress || $requested
);